import _ from 'lodash';
import * as LighthouseService from 'common/services/lighthouse.service';
import BrowserStorageAccessor from 'common/utils/BrowserStorageAccessor';
import AppRouter from 'common/AppRouter';
import * as TrackingService from 'common/services/tracking.service';
import {
  ARTICLE_IDS,
  LOCAL_STORAGE_KEYS,
  PROVIDERS_MAP,
  TRACKING
} from 'common/constants/constants';
import language from 'common/language/en.lang';
import {
  ButtonDontShowAgain,
  getConnectCollaborationsSections,
  getDefaultSections,
  getPMSections
} from './sections';
import jiraLogo from 'images/logos/jira-logo.svg';
import shortcutLogo from 'images/logos/shortcut-logo.svg';
import { createFavouritePmToolsOptionsModal } from 'modals/modal-options-factory';

const PROJECT_MANAGEMENT_PATH = AppRouter.PROJECT_MANAGEMENT.path;
const { text, jiraButton, clubhouseButton, azureBoardsButton } =
  language.Home.ProcessingBar;
const { why } = language.General;
const lang = { text, jiraButton, why };
const localStorageAccessor = BrowserStorageAccessor().local;

export const ProcessingBarStates = {
  PROCESSING_CONTRIBUTORS: 'PROCESSING_CONTRIBUTORS',
  CONNECT_PM: 'CONNECT_PM',
  CONNECT_SLACK: 'CONNECT_SLACK',
  ALL_CONNECTED: 'ALL_CONNECTED',
  PROCESSING_REPOS: 'PROCESSING_REPOS'
};
//disableOnClicks is a prop for storybook.
export const getProcessingStateToContentProps = ({
  history,
  isTeamLicense,
  isFinishedOnBoarding,
  hideProcessingBar,
  disableOnClicks = false,
  isProcessingRepos,
  isSlackConnected,
  selectedTeamId,
  isSlackOnboardingFirstStepFFOn,
  toggleModal
}) => {
  const isProcessing = !isFinishedOnBoarding || isProcessingRepos;

  const getOnCloseClick = (localStorageKey, options = {}) => {
    const onClose = _.get(options, 'events.onClose', _.noop);

    return isProcessing
      ? null
      : () => {
          if (disableOnClicks) {
            return;
          }
          TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
            page: TRACKING.PAGES.DASHBOARD,
            click_type: TRACKING.CLICK.DISMISS
          });
          localStorageAccessor.set(localStorageKey, true);
          hideProcessingBar();
          onClose();
        };
  };

  return {
    [ProcessingBarStates.PROCESSING_CONTRIBUTORS]: {
      sections: getDefaultSections({
        text: lang.text.processingContributors(),
        isProcessing
      }),
      isProcessing
    },
    [ProcessingBarStates.CONNECT_PM]: {
      sections: getPMSections({
        isProcessing,
        text: isFinishedOnBoarding
          ? lang.text.connectPM.default()
          : lang.text.connectPM.processing(),
        jiraButton: {
          text: jiraButton.connect(),
          onClick: disableOnClicks
            ? _.noop
            : () => {
                TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
                  page: TRACKING.PAGES.DASHBOARD,
                  click_type: TRACKING.CLICK.CONNECT_JIRA
                });
                history.push({
                  pathname: `${PROJECT_MANAGEMENT_PATH}/jira`,
                  ...(!isTeamLicense ? { search: 'isOrgSettings=true' } : {})
                });
              },
          logo: jiraLogo
        },
        clubhouseButton: {
          text: clubhouseButton.connect(),
          onClick: disableOnClicks
            ? _.noop
            : () => {
                TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
                  page: TRACKING.PAGES.DASHBOARD,
                  click_type: TRACKING.CLICK.CONNECT_CLUBHOUSE
                });
                history.push({
                  pathname: `${PROJECT_MANAGEMENT_PATH}/clubhouse`,
                  ...(!isTeamLicense ? { search: 'isOrgSettings=true' } : {})
                });
              },
          logo: shortcutLogo
        },
        azureBoardsButton: {
          text: azureBoardsButton.connect(),
          onClick: disableOnClicks
            ? _.noop
            : () => {
                TrackingService.logEvent(TRACKING.EVENTS.CLICK, {
                  page: TRACKING.PAGES.DASHBOARD,
                  click_type: TRACKING.CLICK.CONNECT_AZURE_BOARDS
                });
                history.push({
                  pathname: `${PROJECT_MANAGEMENT_PATH}/azure_boards`,
                  ...(!isTeamLicense ? { search: 'isOrgSettings=true' } : {})
                });
              },
          logo: PROVIDERS_MAP.azure_boards.logo
        },
        link: {
          text: why(),
          onClick: disableOnClicks
            ? _.noop
            : () => LighthouseService.navigateArticle(ARTICLE_IDS.CONNECT_PM)
        },
        onCloseClick: getOnCloseClick(LOCAL_STORAGE_KEYS.CLOSED_PM_BAR)
      }),
      isProcessing,
      onCloseClick: getOnCloseClick(LOCAL_STORAGE_KEYS.CLOSED_PM_BAR, {
        events: {
          onClose: () => {
            toggleModal(createFavouritePmToolsOptionsModal());
          }
        }
      }),
      CustomCloseButton: props => <ButtonDontShowAgain {...props} />
    },
    [ProcessingBarStates.ALL_CONNECTED]: {
      sections: getDefaultSections({
        text: lang.text.allConnected.default(),
        isProcessing
      }),
      isProcessing
    },
    [ProcessingBarStates.PROCESSING_REPOS]: {
      isProcessing,
      sections: getDefaultSections({
        text: lang.text.processingContributors(),
        isProcessing
      })
    },
    [ProcessingBarStates.CONNECT_SLACK]: {
      isProcessing,
      sections: getConnectCollaborationsSections({
        isProcessing,
        isSlackConnected,
        selectedTeamId,
        collaborationAsFirstStep: isSlackOnboardingFirstStepFFOn
      }),
      onCloseClick: getOnCloseClick(LOCAL_STORAGE_KEYS.CLOSED_SLACK_BAR)
    }
  };
};

export const getProcessingState = ({
  isTeamLicense,
  isSyncContributorsCompleted,
  isProjectManagementConnected,
  isFinishedOnBoarding,
  isProcessingRepos,
  isSlackConnected,
  isSlackOnboardingFFOn,
  isSlackDissmissed,
  isSlackOnboardingFirstStepFFOn = false,
  isAllControbutorsTeamSelected
}) => {
  const allConnectedState =
    isProcessingRepos || !isFinishedOnBoarding
      ? ProcessingBarStates.PROCESSING_REPOS
      : ProcessingBarStates.ALL_CONNECTED;

  if (isTeamLicense && !isFinishedOnBoarding && !isSyncContributorsCompleted) {
    return ProcessingBarStates.PROCESSING_CONTRIBUTORS;
  }

  if (
    isSlackOnboardingFirstStepFFOn &&
    isSlackOnboardingFFOn &&
    !isSlackConnected &&
    !isSlackDissmissed &&
    !isAllControbutorsTeamSelected
  ) {
    return ProcessingBarStates.CONNECT_SLACK;
  }

  if (
    !isTeamLicense &&
    !isProjectManagementConnected &&
    !isAllControbutorsTeamSelected
  ) {
    return ProcessingBarStates.CONNECT_PM;
  }

  if (
    !isSlackOnboardingFirstStepFFOn &&
    isSlackOnboardingFFOn &&
    isProjectManagementConnected &&
    !isSlackConnected &&
    !isSlackDissmissed &&
    !isAllControbutorsTeamSelected
  ) {
    return ProcessingBarStates.CONNECT_SLACK;
  }

  return allConnectedState;
};

export const getProcessingBarProps = ({
  isTeamLicense,
  isSyncContributorsCompleted,
  isProjectManagementConnected,
  history,
  isFinishedOnBoarding,
  hideProcessingBar,
  isProcessingRepos,
  isSlackOnboardingFFOn,
  isSlackConnected,
  selectedTeamId,
  isSlackOnboardingFirstStepFFOn,
  isSlackDissmissed,
  toggleModal,
  isAllControbutorsTeamSelected
}) => {
  const processingStateToProps = getProcessingStateToContentProps({
    history,
    isTeamLicense,
    isFinishedOnBoarding,
    hideProcessingBar,
    isProcessingRepos,
    isSlackConnected,
    selectedTeamId,
    isSlackOnboardingFirstStepFFOn,
    toggleModal
  });
  return processingStateToProps[
    getProcessingState({
      isTeamLicense,
      isSyncContributorsCompleted,
      isProjectManagementConnected,
      isFinishedOnBoarding,
      isProcessingRepos,
      isSlackOnboardingFFOn,
      isSlackConnected,
      isSlackOnboardingFirstStepFFOn,
      isSlackDissmissed,
      isAllControbutorsTeamSelected
    })
  ];
};
